import { Box, Link, Text, VStack } from '@chakra-ui/react'
import { StoreLocationMapPreview } from '@/components/forms/parentTicket/feDispatchUtils'
import { useThemeColors } from '@/hooks/useThemeColors'
import PropertyRow from './PropertyRow'

export type StoreMapDetails = {
  locationType?: 'store' | 'location' | null
  businessUnitName?: string | null
  storeName?: string | null
  branchCode?: string | null
  contactPerson?: string | null
  email?: string | null
  contactNumber?: string | null
  storeAddress?: string | null
  latitude?: number | null
  longitude?: number | null
  locationSource?: 'geocoded' | 'fallback' | null
  municipality?: string | null
  province?: string | null
  country?: string | null
}

function addressMapsSearchUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
}

function StoreMapPlaceholder({
  storeAddress,
  locationSource,
}: {
  storeAddress?: string | null
  locationSource?: 'geocoded' | 'fallback' | null
}) {
  const colors = useThemeColors()
  const address = storeAddress?.trim()

  return (
    <Box
      mb={3}
      mx={-3.5}
      px={3.5}
      py={2.5}
      borderBottomWidth="1px"
      borderColor={colors.border}
      bg={colors.sectionBg ?? 'gray.50'}
    >
      {address ? (
        <VStack align="stretch" spacing={1.5}>
          <Text fontSize="xs" color={colors.textSecondary} noOfLines={2}>
            {address}
          </Text>
          <Link href={addressMapsSearchUrl(address)} isExternal fontSize="xs" color={colors.primary}>
            Open address in maps
          </Link>
        </VStack>
      ) : (
        <Text fontSize="sm" color={colors.textMuted}>
          Location not available
        </Text>
      )}
      {locationSource === 'fallback' ? (
        <Text fontSize="2xs" color={colors.textMuted} mt={1.5}>
          Approximate location
        </Text>
      ) : null}
    </Box>
  )
}

function branchLabel(storeDetails: StoreMapDetails): string {
  if (storeDetails.locationType === 'location') {
    return storeDetails.storeName ?? storeDetails.storeAddress ?? '—'
  }

  const parts = [
    storeDetails.branchCode ? `Store ${storeDetails.branchCode}` : null,
    storeDetails.storeName,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : '—'
}

function StoreMap({
  latitude,
  longitude,
  locationSource,
  title,
}: {
  latitude: number
  longitude: number
  locationSource?: 'geocoded' | 'fallback' | null
  title: string
}) {
  const colors = useThemeColors()
  const coordsLabel = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
  const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`

  return (
    <Box mx={-3.5} mb={3} borderBottomWidth="1px" borderColor={colors.border}>
      <StoreLocationMapPreview
        latitude={latitude}
        longitude={longitude}
        height={200}
        title={title}
        mt={0}
        borderColor={colors.border}
        overlay={
          <>
            <Link
              href={mapsUrl}
              isExternal
              position="absolute"
              bottom={2}
              left={2}
              zIndex={1}
              fontSize="11px"
              px={2}
              py={0.5}
              borderRadius="md"
              bg={colors.cardBg}
              borderWidth="1px"
              borderColor={colors.border}
              color={colors.textSecondary}
              _hover={{ textDecoration: 'none', color: colors.text }}
            >
              Open in maps
            </Link>
            <Text
              position="absolute"
              bottom={2}
              right={2}
              zIndex={1}
              fontSize="11px"
              px={2}
              py={0.5}
              borderRadius="md"
              bg={colors.cardBg}
              borderWidth="1px"
              borderColor={colors.border}
              color={colors.textSecondary}
            >
              {coordsLabel}
            </Text>
            {locationSource === 'fallback' ? (
              <Text
                position="absolute"
                top={2}
                right={2}
                zIndex={1}
                fontSize="10px"
                px={2}
                py={0.5}
                borderRadius="md"
                bg={colors.cardBg}
                borderWidth="1px"
                borderColor={colors.border}
                color={colors.textMuted}
              >
                Approximate
              </Text>
            ) : null}
          </>
        }
      />
    </Box>
  )
}

type Props = {
  storeDetails: StoreMapDetails
}

export default function StoreMapCard({ storeDetails }: Props) {
  const colors = useThemeColors()
  const lat = storeDetails.latitude
  const lng = storeDetails.longitude
  const hasCoords =
    lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)

  return (
    <VStack align="stretch" spacing={0}>
      {hasCoords ? (
        <StoreMap
          latitude={lat}
          longitude={lng}
          locationSource={storeDetails.locationSource}
          title={storeDetails.locationType === 'location' ? 'Incident location' : 'Store location'}
        />
      ) : (
        <StoreMapPlaceholder
          storeAddress={storeDetails.storeAddress}
          locationSource={storeDetails.locationSource}
        />
      )}
      {storeDetails.locationType === 'location' ? null : (
        <PropertyRow label="Business unit" value={storeDetails.businessUnitName ?? '—'} />
      )}
      <PropertyRow label={storeDetails.locationType === 'location' ? 'Location' : 'Branch'} value={branchLabel(storeDetails)} />
      {storeDetails.locationType === 'location' ? (
        <>
          {storeDetails.municipality ? (
            <PropertyRow label="Municipality" value={storeDetails.municipality} />
          ) : null}
          {storeDetails.province ? (
            <PropertyRow label="Province" value={storeDetails.province} />
          ) : null}
          {storeDetails.country ? (
            <PropertyRow label="Country" value={storeDetails.country} />
          ) : null}
        </>
      ) : (
        <>
          <PropertyRow label="Contact person" value={storeDetails.contactPerson ?? '—'} />
          <PropertyRow label="Contact number" value={storeDetails.contactNumber ?? '—'} />
          <PropertyRow
            label="Email"
            value={
              storeDetails.email ? (
                <Link href={`mailto:${storeDetails.email}`} fontSize="xs" color={colors.primary}>
                  {storeDetails.email}
                </Link>
              ) : (
                '—'
              )
            }
          />
        </>
      )}
      <PropertyRow label="Address" value={storeDetails.storeAddress ?? '—'} />
    </VStack>
  )
}
